import type {
  PendingVisitData,
  VisitClinicConsent,
  VisitClinicDayVisit,
  VisitClinicRezept,
} from '@/types/visitClinic.types';
import { resolveR2TreatmentPartLabelsFromStoredConsent } from '@/utils/consentForm';
import { hasConsentDifferenceReason } from './consentDifferenceReason';
import { getR2MassagePartsForDate, getR2OrthomorphiaPartsForDate } from './VisitClinicDetailModal';

/**
 * HIT-15106 (BD 1.3.2 S25, 2026/11/05) — the 料金 tables that still need their
 * 「当月施術内容が同意書と異なる理由」: the table has a saved day whose マッサージ / 変形徒手矯正
 * parts differ from the 同意書 (温罨法 is not compared — it follows マッサージ), its reason box
 * is empty (whitespace only counts as empty, what is typed but not saved yet counts), and its
 * rezept is not published (the box is disabled then). After a 「登録」 that changed parts the
 * first one is scrolled to and focused; the box gets no red border (the reason is not mandatory).
 */

function sameParts(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const part of a) if (!b.has(part)) return false;
  return true;
}

/**
 * Do this day's parts differ from the 同意書 covering it? null on a visit means "as the 同意書".
 */
export function r2PartsDifferFromConsent(
  date: string,
  massage: string | null | undefined,
  orthomorphia: string | null | undefined,
  consents: VisitClinicConsent[],
): boolean {
  const consentMassage = getR2MassagePartsForDate(date, consents);
  const consentOrthomorphia = getR2OrthomorphiaPartsForDate(date, consents);
  const dayMassage =
    massage === null || massage === undefined
      ? consentMassage
      : resolveR2TreatmentPartLabelsFromStoredConsent(massage, null);
  const dayOrthomorphia =
    orthomorphia === null || orthomorphia === undefined
      ? consentOrthomorphia
      : resolveR2TreatmentPartLabelsFromStoredConsent(null, orthomorphia);
  return !sameParts(dayMassage, consentMassage) || !sameParts(dayOrthomorphia, consentOrthomorphia);
}

/** Ids of the 料金 tables that still need their reason, in display order. */
export function reasonRequiredRezeptIds(
  yearmonth: string,
  rezepts: VisitClinicRezept[],
  visits: Record<string, VisitClinicDayVisit>,
  consents: VisitClinicConsent[],
): number[] {
  const differing = new Set<number>();
  Object.entries(visits).forEach(([day, visit]) => {
    if (!visit?.rezept_id) return;
    const date = `${yearmonth}-${String(day).padStart(2, '0')}`;
    if (r2PartsDifferFromConsent(date, visit.massage, visit.orthomorphia, consents)) {
      differing.add(Number(visit.rezept_id));
    }
  });
  return rezepts
    .filter(r => differing.has(r.id) && r.all_status === 0 && !hasConsentDifferenceReason(r))
    .map(r => r.id);
}

/**
 * Does this 「登録」 change any day's マッサージ / 変形徒手矯正 parts? Only then is the reason box
 * scrolled to and focused afterwards.
 */
export function storeChangesR2Parts(
  pending: Record<string, PendingVisitData>,
  visits: Record<string, VisitClinicDayVisit>,
  consents: VisitClinicConsent[],
): boolean {
  return Object.entries(pending).some(([date, pd]) => {
    if (!pd.is_dirty || pd.is_deleted) return false;
    const saved = visits[String(Number(date.slice(8, 10)))] ?? null;
    const before = { massage: saved?.massage ?? null, orthomorphia: saved?.orthomorphia ?? null };
    const resolve = (massage: string | null | undefined, orthomorphia: string | null | undefined) => ({
      m: massage === null || massage === undefined
        ? getR2MassagePartsForDate(date, consents)
        : resolveR2TreatmentPartLabelsFromStoredConsent(massage, null),
      o: orthomorphia === null || orthomorphia === undefined
        ? getR2OrthomorphiaPartsForDate(date, consents)
        : resolveR2TreatmentPartLabelsFromStoredConsent(null, orthomorphia),
    });
    const a = resolve(before.massage, before.orthomorphia);
    const b = resolve(pd.massage, pd.orthomorphia);
    return !sameParts(a.m, b.m) || !sameParts(a.o, b.o);
  });
}
